import { useEffect, useState } from "react";
import { Share2, Copy, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n/i18n";

const linkOf = (s) => `${window.location.origin}/favoritos/${s.token}`;

const copy = async (s, t) => {
  try { await navigator.clipboard.writeText(linkOf(s)); toast.success(t("Enlace copiado")); } catch { toast.message(linkOf(s)); }
};

const CreateDialog = ({ open, onOpenChange, onCreated }) => {
  const { t } = useI18n();
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState(false);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/company/shares", { label, includeNotes: notes });
      onCreated(data); copy(data, t); setLabel(""); setNotes(false); onOpenChange(false);
    } catch (e) { toast.error(t(apiError(e).message)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[28px]" data-testid="share-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">{t("Compartir favoritos")}</DialogTitle>
          <DialogDescription>{t("Quien tenga el enlace verá el perfil y el CV de los favoritos actuales durante 7 días, sin necesidad de cuenta.")}</DialogDescription>
        </DialogHeader>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} placeholder={t("Nombre de la lista (opcional)")} className="h-11 rounded-xl" data-testid="share-label-input" />
        <label className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 p-4 text-sm">
          <span><span className="font-semibold">{t("Incluir notas del equipo")}</span><span className="block text-xs text-slate-500">{t("Las notas internas también serán visibles.")}</span></span>
          <Switch checked={notes} onCheckedChange={setNotes} data-testid="share-include-notes" />
        </label>
        <button onClick={create} disabled={busy} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-upv font-bold text-white hover:bg-upv-dark disabled:opacity-60" data-testid="share-create-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} {t("Crear y copiar enlace")}</button>
      </DialogContent>
    </Dialog>
  );
};

const ShareRow = ({ s, onRevoke }) => {
  const { t, lang } = useI18n();
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`share-row-${s.id}`}>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">{s.label || t("Candidatos favoritos")}{s.includeNotes && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{t("con notas")}</span>}</p>
        <p className="text-xs text-slate-500" data-testid={`share-status-${s.id}`}>{s.active ? t("Activo hasta el {d}", { d: new Date(s.expiresAt).toLocaleDateString(lang) }) : t(s.revoked ? "Desactivado" : "Caducado")} · {t("{n} visitas", { n: s.views || 0 })} · {s.createdByName}</p>
      </div>
      {s.active && (
        <div className="flex gap-2">
          <button onClick={() => copy(s, t)} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-bold hover:border-slate-900" data-testid={`share-copy-${s.id}`}><Copy className="h-3.5 w-3.5" /> {t("Copiar")}</button>
          {s.canRevoke && <button onClick={() => onRevoke(s)} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 px-3 text-xs font-bold text-upv hover:border-upv" data-testid={`share-revoke-${s.id}`}><Ban className="h-3.5 w-3.5" /> {t("Desactivar")}</button>}
        </div>
      )}
    </li>
  );
};

export const ShareFavorites = ({ favorites }) => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const active = items.filter((s) => s.active);
  const old = items.filter((s) => !s.active);
  useEffect(() => { api.get("/company/shares").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  const revoke = async (s) => {
    try { await api.delete(`/company/shares/${s.id}`); setItems((xs) => xs.map((x) => (x.id === s.id ? { ...x, active: false, revoked: true } : x))); toast.success(t("Enlace desactivado")); } catch (e) { toast.error(t(apiError(e).message)); }
  };
  return (
    <div className="rounded-[24px] border border-slate-200 bg-white p-6" data-testid="share-favorites">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-400"><Share2 className="h-3.5 w-3.5" /> {t("Compartir favoritos")}</p>
          <p className="mt-1 text-sm text-slate-600">{t("Envía la lista a un compañero con un enlace seguro. Siempre muestra los favoritos actuales.")}</p>
        </div>
        <button onClick={() => setOpen(true)} disabled={!favorites} className="flex h-11 shrink-0 items-center gap-2 rounded-xl bg-slate-950 px-5 text-sm font-bold text-white disabled:opacity-40" data-testid="share-create-button"><Share2 className="h-4 w-4" /> {t("Crear enlace")}</button>
      </div>
      {active.length > 0 && <ul className="mt-4 divide-y divide-slate-100">{active.map((s) => <ShareRow key={s.id} s={s} onRevoke={revoke} />)}</ul>}
      {old.length > 0 && <button onClick={() => setShowOld((v) => !v)} className="mt-3 text-xs font-semibold text-slate-500 hover:text-slate-900" data-testid="share-toggle-inactive">{t(showOld ? "Ocultar enlaces inactivos" : "Ver {n} enlaces inactivos", { n: old.length })}</button>}
      {showOld && <ul className="mt-2 divide-y divide-slate-100 opacity-70">{old.map((s) => <ShareRow key={s.id} s={s} onRevoke={revoke} />)}</ul>}
      <CreateDialog open={open} onOpenChange={setOpen} onCreated={(s) => setItems((xs) => [s, ...xs])} />
    </div>
  );
};
